// src/store/modules/notifications/index.js
import api from "./api";
import { isTransientNetworkError } from "@/utils/networkErrors";

const state = () => ({
  items: [],
  unseenCount: 0,
  unreadCount: 0,
  loading: false,
  page: 1,
  totalPages: 1,
  hasMore: false,
});

const getters = {
  notifications: (s) => s.items,
  unseenCount: (s) => s.unseenCount,
  unreadCount: (s) => s.unreadCount,
  notificationsHasMore: (s) => s.hasMore,
};

const mutations = {
  SET_LOADING(s, v) { s.loading = v; },
  SET_NOTIFICATIONS(s, { items, unseenCount, unreadCount, page, totalPages }) {
    s.items = items;
    s.unseenCount = unseenCount;
    s.unreadCount = unreadCount;
    s.page = page;
    s.totalPages = totalPages;
    s.hasMore = page < totalPages;
  },
  APPEND_NOTIFICATIONS(s, { items, page, totalPages }) {
    s.items = s.items.concat(items);
    s.page = page;
    s.totalPages = totalPages;
    s.hasMore = page < totalPages;
  },
  PREPEND_NOTIFICATION(s, { notification, unseenCount }) {
    const isNew = !s.items.some((n) => n.id === notification.id);
    if (isNew) s.items = [notification, ...s.items];
    if (typeof unseenCount === "number") s.unseenCount = unseenCount;
    else if (isNew) s.unseenCount += 1;
    if (isNew && !notification.read_at) s.unreadCount += 1;
  },
  SET_COUNTS(s, { unseenCount, unreadCount }) {
    if (typeof unseenCount === "number") s.unseenCount = unseenCount;
    if (typeof unreadCount === "number") s.unreadCount = unreadCount;
  },
  MARK_ALL_READ(s) {
    s.items = s.items.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
  },
  MARK_ALL_SEEN(s) { s.unseenCount = 0; },
  MARK_READ(s, id) {
    const n = s.items.find((x) => x.id === id);
    if (n && !n.read_at) { n.read_at = new Date().toISOString(); }
  },
};

const actions = {
  async fetchNotifications({ commit }, { page = 1, perPage = 20, append = false } = {}) {
    commit("SET_LOADING", true);
    try {
      const { items, meta } = await api.fetch({ page, perPage });
      const payload = {
        items,
        unseenCount: meta.unseen_count || 0,
        unreadCount: meta.unread_count || 0,
        page: meta.page || page,
        totalPages: meta.total_pages || 1,
      };
      commit(append ? "APPEND_NOTIFICATIONS" : "SET_NOTIFICATIONS", payload);
    } catch (error) {
      // Оба вызова экшена — фоновые и fire-and-forget (NotificationBell.created,
      // NotificationsPage.created/loadMore), .catch на них ставить негде без
      // копипасты. Инцидент 2026-09-08 (Rollbar f52f91d8): свёрнутое на Android
      // приложение потеряло сеть, реджект всплыл unhandled и лёг в Rollbar
      // uncaught-ошибкой уровня error. Обрыв/таймаут гасим здесь: о состоянии
      // сети говорит глобальный баннер (createNetworkFlagInterceptor), список
      // просто остаётся прежним. Всё остальное пробрасываем — это настоящие
      // дефекты, и они обязаны доехать до Rollbar.
      if (!isTransientNetworkError(error)) throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async markSeen({ commit }) {
    const meta = await api.markSeen();
    commit("MARK_ALL_SEEN");
    commit("SET_COUNTS", { unseenCount: meta.unseen_count, unreadCount: meta.unread_count });
  },
  async markRead({ commit }, id) {
    const meta = await api.markRead(id);
    commit("MARK_READ", id);
    commit("SET_COUNTS", { unseenCount: meta.unseen_count, unreadCount: meta.unread_count });
  },
  async markAllRead({ commit }) {
    const meta = await api.markAllRead();
    commit("MARK_ALL_READ");
    commit("SET_COUNTS", { unseenCount: meta.unseen_count, unreadCount: meta.unread_count });
  },
  prependNotification({ commit }, payload) {
    commit("PREPEND_NOTIFICATION", payload);
  },
};

export default { namespaced: true, state, getters, mutations, actions };
